import React, { useState } from 'react'
import PropTypes from 'prop-types'

// Below this share a segment is too narrow to hold its label.
const MIN_INLINE_LABEL_WIDTH = 8

const defaultFormatValue = (value) => (value > 0 && value < 1 ? '<1%' : `${Math.round(value)}%`)

// Keeps the tooltip of the outermost segments inside the card.
const clampTooltipPosition = (position) => Math.min(Math.max(position, 4), 96)

// Roughly half the width of the summary bubble, as a share of the bar. Closer
// to an end than this a centered bubble would hang out of the card.
const SUMMARY_ANCHOR_MARGIN = 10

/**
 * Where the summary bubble hangs off the spot it marks: centered on it, or —
 * for a bar that is (almost) full or (almost) empty — flush with that end of
 * the bar, so it stays inside the card.
 */
const summaryAnchor = (filledShare) => {
  if (filledShare >= 100 - SUMMARY_ANCHOR_MARGIN) return 'end'
  if (filledShare <= SUMMARY_ANCHOR_MARGIN) return 'start'
  return 'center'
}

const DARK_TEXT = '#11344a' // $navy-125
const LIGHT_TEXT = '#ffffff'

// Default color of the summary bubble, kept in sync with the stylesheet.
const SUMMARY_COLOR = '#11344a' // $navy-125

/**
 * Readable text color on top of a segment color. Light segments (free, reserved)
 * need dark text, the darker ones white.
 */
const readableTextColor = (background) => {
  const hex = typeof background === 'string' ? background.replace('#', '') : ''
  if (hex.length !== 6) return LIGHT_TEXT

  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
  const channel = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)

  // Threshold picked so every segment color keeps a contrast ratio of 3.5:1 or better.
  return luminance > 0.25 ? DARK_TEXT : LIGHT_TEXT
}

/**
 * One single 100% wide bar, split into segments. Each segment shows its own
 * share, narrow segments are labeled in the legend only. Hovering a segment
 * reveals its name and share.
 * @Tags( shared, presentational )
 */
export default function StackedBar({
  data,
  formatValue = defaultFormatValue,
  summaryLabel = '',
  summaryColor = SUMMARY_COLOR,
}) {
  const [hovered, setHovered] = useState(null)

  if (!data || !data.length) return null

  // Center of every segment in % of the whole bar, used to place the tooltip.
  let offset = 0
  const centers = data.map((segment) => {
    const center = offset + segment.y / 2
    offset += segment.y
    return center
  })

  // The whole filled part, everything that is not the empty remainder. Doubles
  // as the spot the summary bubble marks: where the filled part ends.
  const filledShare = data.reduce((sum, segment) => (segment.empty ? sum : sum + segment.y), 0)
  const showSummary = Boolean(summaryLabel) && filledShare > 0
  const summaryAnchorSide = summaryAnchor(filledShare)

  return (
    <div className={`stacked-bar ${showSummary ? 'stacked-bar--with-summary' : ''}`}>
      <div className="stacked-bar__track" onMouseLeave={() => setHovered(null)}>
        {showSummary && (
          <div
            className={`stacked-bar__summary stacked-bar__summary--${summaryAnchorSide} ${hovered !== null ? 'stacked-bar__summary--dimmed' : ''}`}
            style={{
              // The anchored bubbles are pinned to an end of the bar by the
              // stylesheet, only the centered one follows the filled share.
              left: summaryAnchorSide === 'center' ? `${filledShare}%` : undefined,
              background: summaryColor,
              // Stays readable whichever color the bubble is given.
              color: readableTextColor(summaryColor),
              '--stacked-bar-summary-bg': summaryColor,
            }}
          >
            <span className="stacked-bar__summary-label">{summaryLabel}</span>
            <strong className="stacked-bar__summary-value">{formatValue(filledShare)}</strong>
          </div>
        )}
        {data.map((segment, index) => (
          <div
            key={`${segment.key}-${index}`}
            className={`stacked-bar__segment ${index === 0 ? 'stacked-bar__segment--first' : ''} ${index === data.length - 1 ? 'stacked-bar__segment--last' : ''} ${segment.empty ? 'stacked-bar__segment--empty' : ''} ${hovered !== null && hovered !== index ? 'stacked-bar__segment--dimmed' : ''} ${segment.colorClass || ''}`}
            style={{ width: `${segment.y}%`, background: segment.color }}
            onMouseEnter={() => setHovered(index)}
          >
            {segment.y >= MIN_INLINE_LABEL_WIDTH && (
              <span
                className="stacked-bar__segment-value"
                style={{ color: readableTextColor(segment.color) }}
              >
                {formatValue(segment.y)}
              </span>
            )}
          </div>
        ))}
        {hovered !== null && (
          <div
            className="stacked-bar__tooltip"
            style={{
              left: `${clampTooltipPosition(centers[hovered])}%`,
              background: data[hovered].color,
              color: readableTextColor(data[hovered].color),
              '--stacked-bar-tooltip-bg': data[hovered].color,
            }}
          >
            {data[hovered].name}
            <strong style={{ color: readableTextColor(data[hovered].color) }}>
              {formatValue(data[hovered].y)}
            </strong>
          </div>
        )}
      </div>
      <ul className="stacked-bar__legend">
        {data.map((segment, index) => (
          <li
            key={`legend-${segment.key}-${index}`}
            className="stacked-bar__legend-item"
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          >
            <span
              className={`stacked-bar__legend-dot ${segment.colorClass || ''}`}
              style={{ background: segment.color }}
            />
            <span className="stacked-bar__legend-name">{segment.name}</span>
            <strong className="stacked-bar__legend-value">{formatValue(segment.y)}</strong>
          </li>
        ))}
      </ul>
    </div>
  )
}

StackedBar.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      y: PropTypes.number,
      color: PropTypes.string,
      colorClass: PropTypes.string,
      // Renders as an outlined "empty" area instead of a filled segment.
      empty: PropTypes.bool,
    }),
  ),
  formatValue: PropTypes.func,
  // Turns on the bubble above the bar showing the filled share, labeled with this.
  summaryLabel: PropTypes.string,
  // Bubble color, the text color follows it.
  summaryColor: PropTypes.string,
}
